import {AudioTrack, UserVideo} from "@/lib/types";
import {generateRandomFileName, uploadFileToStorage} from "@/lib/FileUpload";
import {firestore, functions, httpsCallable} from "@/firebase/firebaseConfig";
import {getLanguageInfoByCode} from "@/lib/LanguageUtils";
import {collection, getDocs} from "firebase/firestore";
import {onSnapshot, query, where} from "@firebase/firestore";

export function uploadAudioTrack(file: File, languageCode: string, userVideo: UserVideo): Promise<AudioTrack> {
    const userVideoId = userVideo?.id

    if (!file) {
        console.error('No file selected. Upload cancelled.')
        return Promise.reject(new Error('No file selected. Upload cancelled.'))
    }
    if (!languageCode) {
        console.error('Language not selected. Upload cancelled.')
        return Promise.reject(new Error('Language not selected. Upload cancelled.'))
    }
    if (file.size > 25000000) {
        return Promise.reject(new Error('File size exceeds 25MB. Please choose a smaller file or compress it before uploading.'))
    }
    if (!userVideoId) {
        return Promise.reject(new Error('User video id not provided. Upload cancelled.'))
    }

    const fileExtension = file.name.split('.').pop()
    const fileType = file.type
    const fileName = generateRandomFileName(22, fileExtension)
    const filePath = `videos/${userVideoId}/audio/originals/${languageCode}/` + fileName

    return uploadFileToStorage(file, filePath, fileType).then((result) => {
        //Let's prepare an AudioTrack object which has all the info about the audio track
        //Then call a cloud function to save the AudioTrack object and process adding it to the video
        //Eventually we will want the API to be able to handle the upload as well.

        console.log('File uploaded to', result.downloadUrl)

        const languageLabel = getLanguageInfoByCode(languageCode)?.nativeLabel || languageCode

        const audioTrack: AudioTrack = {
            userVideoId: userVideoId,
            userVideoVersion: userVideo?.version || 1,
            url: result.downloadUrl,
            languageCode: languageCode,
            label: languageLabel,
            size: file.size,
            fileType: fileType,
            fileName: fileName,
            originalFileName: file?.name,
            storageFullPath: result.fileRef.fullPath,
            storageBucket: result.fileRef.bucket,
            storagePath: result.fileRef.name,
            dateCreated: new Date(),
            lastUpdated: new Date,
        }

        return addAudioTrack(audioTrack)
    })
}

export function addAudioTrack(audioTrack: AudioTrack): Promise<AudioTrack> {
    const addAudioTrack = httpsCallable(functions, 'video-addAudioTrack');
    return addAudioTrack({audioTrack}).then((result) => {
        console.log('Called video-addAudioTrack with result', result)
        return audioTrack
    })
}

export function getAudioTrack(audioTrackId: string): Promise<string> {
    return Promise.resolve("Audio Track")
}

export function listAudioTracksForVideo(userVideoId: string, languageOrder?: string[]): Promise<AudioTrack[]> {
    console.log('Start listAudioTrack function')
    return getDocs(query(collection(firestore, 'AudioTracks'), where("userVideoId", "==", userVideoId)))
        .then((results) => {
            console.log('Retrieved audio tracks')
            const tracks: AudioTrack[] = []
            results.forEach((doc) => {
                const track = {...doc.data(), id: doc.id} as AudioTrack
                if (track) {
                    tracks.push(track)
                }
            })
            if (languageOrder) {
                // Go through the tracks list and see if there are any tracks that are not in the languageOrder array
                // If so then add the languageCode to an array called unSortedLanguages
                const unsortedLanguages: string[] = []
                tracks.forEach((track) => {
                    if (languageOrder.indexOf(track.languageCode) === -1) {
                        unsortedLanguages.push(track.languageCode)
                    }
                })

                // Now sort the unsorted language codes alphabetically
                unsortedLanguages.sort()

                // Append unsortedLanguages to end of languageOrder
                languageOrder.push(...unsortedLanguages)

                console.log('Sorting audio tracks based on language order')
                tracks.sort((a: AudioTrack, b: AudioTrack) => {
                    if (languageOrder.indexOf(b.languageCode) === -1) return -1;
                    if (languageOrder.indexOf(a.languageCode) === -1) return 1;
                    return languageOrder.indexOf(a.languageCode) - languageOrder.indexOf(b.languageCode);
                })
            }
            console.log('Returning audio tracks', tracks)
            return tracks
        })
}

export function updateAudioTrack(audioTrackId: string, audioTrack: any): Promise<string> {
    return Promise.resolve("Audio Track Updated")
}

export function removeAudioTrack(audioTrackId: string): Promise<void> {
    const removeAudioTrack = httpsCallable(functions, 'video-removeAudioTrack');
    return removeAudioTrack({audioTrackId})
        .then(() => {
            console.log('Removed audio track with ID', audioTrackId);
        })
        .catch((error) => {
            console.error('Error removing audio track', error);
            throw new Error('Error removing audio track');
        });
}

// Listener for changes to AudioTracks for a video
export function subscribeToAudioTracksChanges(userVideoId: string, callback: () => void): () => void {
    const q = query(collection(firestore, 'AudioTracks'), where("userVideoId", "==", userVideoId))
    return onSnapshot(q, (snapshot) => {
        console.log('AudioTracks Snapshot', snapshot)
        callback && callback()
    })
}

export function getAudioTrackByUserVideoIdAndLanguageCode(userVideoId: string, languageCode: string): Promise<AudioTrack | null> {
    return getDocs(query(collection(firestore, 'AudioTracks'), where("userVideoId", "==", userVideoId), where("languageCode", "==", languageCode)))
        .then((results) => {
            if (results.empty) {
                return null
            }
            return {...results.docs[0].data(), id: results.docs[0].id} as AudioTrack
        }).catch(err => {
            console.error('Error getting audio track by user video id and language code', err)
            return null
        })
}
